import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuthInfo } from '@propelauth/react';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { user: authInfoUser, orgHelper, accessHelper, loading: propelAuthLoading } = useAuthInfo(); // Get loading from propelAuth
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSiteAdmin, setIsSiteAdmin] = useState(false);
  const [userOrgs, setUserOrgs] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rubric, setRubric] = useState('coachable');
  const [hasPipeline, setHasPipeline] = useState(false);
  const [userRole, setUserRole] = useState('undefined');

  // Helper to determine which org to set as current
  // Tries to restore the org from localStorage if possible,
  // otherwise defaults to the first org in the list.
  const getOrgToSet = (orgs) => {
    const savedOrgId = localStorage.getItem('currentOrgId');
    if (savedOrgId) {
      // Try to find the org matching the saved orgId
      const found = orgs.find(org => org.orgId === savedOrgId);
      if (found) return found;
    }
    // If no saved org or not found, use the first org if available
    if (orgs.length > 0) {
      return orgs[0];
    }
    // No orgs available
    return null;
  };

  // Update loading state based on PropelAuth loading
  useEffect(() => {
    setLoading(propelAuthLoading);
  }, [propelAuthLoading]);

  // Separate site admin check from org logic
  useEffect(() => {
    if (authInfoUser) {
      // Site admin check by email - ONLY use authenticated user email
      const siteAdminEmails = [
        'dan@chak.org',
        'justin@joincoachable.ai',
        'roger@rabbits.com',
      ];
      
      const userIsSiteAdmin = siteAdminEmails.includes(authInfoUser.email);
      setIsSiteAdmin(userIsSiteAdmin);
    } else {
      setIsSiteAdmin(false);
    }
  }, [authInfoUser]);

  // Organization logic
  useEffect(() => {
    if (authInfoUser && orgHelper) {
      const orgs = orgHelper.getOrgs();
      setUserOrgs(orgs);

      // Use helper to get orgToSet
      const orgToSet = getOrgToSet(orgs);
      if (orgToSet) {
        setCurrentOrg(orgToSet);
      } 
    }
  }, [authInfoUser, orgHelper]);

  // Log userRole when authInfoUser is available
  useEffect(() => {
    if (authInfoUser) {
      const role = authInfoUser.properties?.userAssignedRole || 'undefined';
      setUserRole(role);
    }
  }, [authInfoUser]);

  // Save currentOrg to localStorage whenever it changes
  useEffect(() => {
    if (currentOrg) {
      localStorage.setItem('currentOrgId', currentOrg.orgId);
      const isAdminOrOwner = accessHelper.isAtLeastRole(currentOrg.orgId, "Admin");
      setIsAdmin(isAdminOrOwner);
      // Rubric logic: set rubric when currentOrg changes
      const orgRubric = currentOrg?.orgMetadata?.rubric;
      setRubric(orgRubric || 'coachable');
      // Set hasPipeline when currentOrg changes
      setHasPipeline(currentOrg?.orgMetadata?.pipeline === "true");
    } else {
      setHasPipeline(false);
    }
  }, [currentOrg, accessHelper]);

  return (
    <AuthContext.Provider value={{ 
      user: authInfoUser,
      authInfoUser, 
      isAdmin, 
      isSiteAdmin, 
      userOrgs, 
      currentOrg, 
      setCurrentOrg, 
      isLoading: loading, 
      rubric, 
      hasPipeline, 
      userRole 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
